import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner"; // Import loader
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"; // Import eye icons
import { StandardURL } from "../App";

const Registration = () => {
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false); // State for toggling password visibility
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false); // Separate toggle for confirmPassword
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    termsAndCondition: false,
  });

  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });

    if (name === "password") {
      validatePassword(value);
    } else if (name === "confirmPassword") {
      validateConfirmPassword(value, formData.password);
    }
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const maxLength = 64;
    const regexNumber = /\d/; // At least one number
    const regexLowerCase = /[a-z]/; // At least one lowercase
    const regexUpperCase = /[A-Z]/; // At least one uppercase
    const regexSpecialChar = /[@#$%^&*(),.?":{}|<>]/; // At least one special character

    let errorMessage = "";

    if (password.length < minLength || password.length > maxLength) {
      errorMessage = `Password must be between ${minLength}-${maxLength} characters.`;
    } else if (!regexNumber.test(password)) {
      errorMessage = "Password must contain at least one number.";
    } else if (!regexLowerCase.test(password)) {
      errorMessage = "Password must contain at least one lowercase letter.";
    } else if (!regexUpperCase.test(password)) {
      errorMessage = "Password must contain at least one uppercase letter.";
    } else if (!regexSpecialChar.test(password)) {
      errorMessage = "Password must contain at least one special character.";
    }

    setErrors((prev) => ({ ...prev, password: errorMessage }));
    return errorMessage === ""; // Return true if valid, false otherwise
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    const errorMessage =
      confirmPassword !== password ? "Passwords do not match." : "";
    setErrors((prev) => ({ ...prev, confirmPassword: errorMessage }));
    return errorMessage === ""; // Return true if valid, false otherwise
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Re-validate passwords before submission
    const isPasswordValid = validatePassword(formData.password);
    const isConfirmPasswordValid = validateConfirmPassword(formData.confirmPassword, formData.password);

    if (!isPasswordValid || !isConfirmPasswordValid || !formData.termsAndCondition) {
        toast.error("Please correct the errors in the form and accept terms & conditions.");
        return;
    }

    setIsLoading(true); // Set loading state to true
    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/auth/register`,
        formData,
        {
          params: {
            accountType: "BUSINESS",
          },
        }
      );
      toast.success(
        "Registration is successful. Kindly check your email to complete verification!"
      );

      setIsLoading(false); // Set loading state to false
      navigate("/"); // Redirect to the login page
    } catch (error) {
      console.error(error); // Display error message
      setIsLoading(false); // Set loading state to false

      if (error.response && error.response.status === 401) {
        toast.error("Unauthorized access.");
      } else if (error.response && error.response.status === 406) {
        toast.error(
          "Wrong password format. Password must contain a number, capital letter, small letter, and a special character (e.g., P@ssw0rd10{})."
        );
      } else {
        toast.error("An error occurred, please try again later.");
      }
    }
  };

  return (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-100 py-12 px-4 sm:px-6 lg:px-8">
    <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-2xl border border-gray-200 transform hover:scale-105 transition-all duration-300 ease-in-out">
      <div className="text-center">
        <h2 className="mt-2 text-4xl font-extrabold text-gray-900 leading-tight">
          Join Us Today! ✨
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Create your account to manage your properties easily.
        </p>
      </div>
      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor="name" className="sr-only">
              Full Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              required
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-base transition duration-200 ease-in-out"
              placeholder="Your Full Name"
              value={formData.name}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="email" className="sr-only">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-base transition duration-200 ease-in-out"
              placeholder="Your Email Address"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          <div>
            <label htmlFor="phoneNumber" className="sr-only">
              Phone Number
            </label>
            <input
              id="phoneNumber"
              name="phoneNumber"
              type="tel"
              autoComplete="tel"
              required
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-base transition duration-200 ease-in-out"
              placeholder="Phone Number (e.g., +2547XXXXXXXX)"
              value={formData.phoneNumber}
              onChange={handleChange}
            />
          </div>
          <div className="relative">
            <label htmlFor="password" className="sr-only">
              Password
            </label>
            <input
              id="password"
              name="password"
              type={passwordVisible ? "text" : "password"}
              autoComplete="new-password"
              required
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-base pr-10 transition duration-200 ease-in-out"
              placeholder="Create your password"
              value={formData.password}
              onChange={handleChange}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none transition duration-200"
              aria-label={passwordVisible ? "Hide password" : "Show password"}
            >
              <FontAwesomeIcon icon={passwordVisible ? faEye : faEyeSlash} className="h-5 w-5" />
            </button>
          </div>
          {errors.password && (
            <p className="text-red-500 text-sm mt-1">{errors.password}</p>
          )}
          <div className="relative">
            <label htmlFor="confirmPassword" className="sr-only">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={confirmPasswordVisible ? "text" : "password"}
              autoComplete="new-password"
              required
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-base pr-10 transition duration-200 ease-in-out"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
            <button
              type="button"
              onClick={toggleConfirmPasswordVisibility}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none transition duration-200"
              aria-label={confirmPasswordVisible ? "Hide password" : "Show password"}
            >
              <FontAwesomeIcon icon={confirmPasswordVisible ? faEye : faEyeSlash} className="h-5 w-5" />
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
          )}
        </div>

        <div className="flex items-center mt-4">
          <input
            id="terms-and-condition"
            name="termsAndCondition"
            type="checkbox"
            checked={formData.termsAndCondition}
            onChange={handleChange}
            className="h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded-md cursor-pointer"
            required
          />
          <label htmlFor="terms-and-condition" className="ml-2 block text-sm text-gray-900 cursor-pointer">
            I agree to the{" "}
            <a href="#" className="font-semibold text-purple-600 hover:text-purple-500 transition-colors duration-200">
              Terms and Conditions
            </a>
          </label>
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading || errors.password || errors.confirmPassword || !formData.termsAndCondition}
            className={`
              relative w-full flex justify-center py-2.5 px-4 border border-transparent text-lg font-semibold rounded-lg shadow-md
              ${isLoading
                ? "bg-purple-400 cursor-not-allowed"
                : "bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transform transition-all duration-200 ease-in-out hover:scale-105"
              }
              text-white
            `}
          >
            {isLoading ? (
              <Oval
                height={24}
                width={24}
                color="white"
                visible={true}
                ariaLabel='oval-loading'
                secondaryColor="rgba(255,255,255,0.4)"
                strokeWidth={5}
                strokeWidthSecondary={5}
              />
            ) : (
              "Register Account"
            )}
          </button>
        </div>

        <div className="text-sm text-center mt-6 text-gray-600">
          Already have an account?{" "}
          <a
            href="/"
            className="font-semibold text-purple-600 hover:text-purple-500 transition-colors duration-200"
          >
            Log In
          </a>
        </div>
      </form>
    </div>
  </div>
);
};

export default Registration;